import { useEffect, useState } from 'react';
import { doesWindowExist } from '../../utils/dom/dom.utils';

type UseScriptOptions = Partial<Omit<HTMLScriptElement, 'src'>> & {
	src: string | null | undefined;
};

const findExistingScript = (src: string) => (doesWindowExist() ? document.querySelector<HTMLScriptElement>(`script[src="${src}"]`) : null);

/**
 * Hook to dynamically inject a script tag. Returns a tuple of [loading, error].
 * If a script with the same `src` already exists in the DOM it will not be added again.
 * If `src` is falsy the script will not be loaded.
 * Any extra properties are forwarded as attributes on the script element.
 */
export const useScript = (options: UseScriptOptions): [boolean, ErrorEvent | null] => {
	const { src, ...attrs } = options;

	const [loading, setLoading] = useState(() => {
		if (!src) return false;
		return !findExistingScript(src);
	});
	const [error, setError] = useState<ErrorEvent | null>(null);

	useEffect(() => {
		if (!src || findExistingScript(src)) {
			setLoading(false);
			return;
		}

		setLoading(true);

		const script = document.createElement('script');
		script.src = src;

		Object.assign(script, attrs);

		const handleLoad = () => {
			setLoading(false);
		};

		const handleError = (event: ErrorEvent) => {
			setLoading(false);
			setError(event);
		};

		script.addEventListener('load', handleLoad);
		script.addEventListener('error', handleError);

		document.body.appendChild(script);

		return () => {
			script.removeEventListener('load', handleLoad);
			script.removeEventListener('error', handleError);
		};
	}, [src]);

	return [loading, error];
};
